import React from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '../../../core/components/BottomNavigation';
import { useMission } from '../hooks/useMission';
import { useDeferredLoading } from '../../../core/hooks/useDeferredLoading';
import { RiderMissionDto, RiderMissionStatus } from '../types/mission.types';
import styles from '../styles/MissionPage.module.css';

const STATUS_LABEL: Record<RiderMissionStatus, string> = {
    UPCOMING: '예정',
    IN_PROGRESS: '진행중',
    COMPLETED: '완료',
    SETTLEMENT_EXPECTED: '정산예정',
    SETTLED: '정산완료',
};

const MISSION_TYPE_LABEL: Record<string, string> = {
    OVERAGE: '초과달성형',
    FIXED: '고정건수형',
    FULL_UNIT: '풀타임형',
};

const PERIOD_TYPE_LABEL: Record<string, string> = {
    DAILY: '일별',
    WEEKLY: '주별',
};

const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return dateStr.replace(/-/g, '.');
};

const MissionCard: React.FC<{ mission: RiderMissionDto }> = ({ mission }) => {
    const isActive = mission.riderStatus === 'IN_PROGRESS' || mission.riderStatus === 'UPCOMING';
    const isSettled = mission.riderStatus === 'SETTLED';
    const showAchievementBadge = mission.riderStatus === 'SETTLED' || mission.riderStatus === 'COMPLETED';
    const isAchieved = mission.riderStatus === 'SETTLED';
    const hasPeriodBreakdown = !!mission.periodStatuses && mission.periodStatuses.length > 0;

    return (
        <div className={`${styles['mission-card']} ${isSettled ? styles['card-settled'] : ''}`}>
            <div className={styles['card-top-row']}>
                <div className={styles['badge-group']}>
                    <span className={`${styles['status-badge']} ${styles[`status-${mission.riderStatus.toLowerCase().replace(/_/g, '-')}`]}`}>
                        {STATUS_LABEL[mission.riderStatus]}
                    </span>
                    {showAchievementBadge && (
                        <span className={`${styles['achievement-badge']} ${isAchieved ? styles['achievement-achieved'] : styles['achievement-not-achieved']}`}>
                            {isAchieved ? '달성' : '미달성'}
                        </span>
                    )}
                </div>
                <span className={styles['period-type']}>{PERIOD_TYPE_LABEL[mission.periodType]} 정산</span>
            </div>

            <div className={styles['card-title']}>{mission.title}</div>

            <div className={styles['card-info-grid']}>
                <div className={styles['info-item']}>
                    <span className={styles['info-label']}>미션 유형</span>
                    <span className={styles['info-value']}>{MISSION_TYPE_LABEL[mission.missionType]}</span>
                </div>
                <div className={styles['info-item']}>
                    <span className={styles['info-label']}>목표 건수</span>
                    <span className={styles['info-value']}>{mission.targetCnt}건</span>
                </div>
                <div className={styles['info-item']}>
                    <span className={styles['info-label']}>건당 보상</span>
                    <span className={`${styles['info-value']} ${styles['reward-value']}`}>
                        {mission.rewardAmt.toLocaleString()}원
                    </span>
                </div>
                {mission.maxCnt > 0 && (
                    <div className={styles['info-item']}>
                        <span className={styles['info-label']}>최대 횟수</span>
                        <span className={styles['info-value']}>{mission.maxCnt}</span>
                    </div>
                )}
            </div>

            <div className={styles['card-date-row']}>
                <span className={styles['date-range']}>
                    <i className="fas fa-calendar-alt" style={{ marginRight: 4 }} />
                    {formatDate(mission.startDate)} ~ {formatDate(mission.endDate)}
                </span>
            </div>

            {hasPeriodBreakdown && (
                <div className={styles['period-breakdown']}>
                    {mission.periodStatuses!.map((ps, idx) => (
                        <div key={idx} className={styles['period-row']}>
                            <span className={styles['period-dates']}>
                                {formatDate(ps.startDate)} ~ {formatDate(ps.endDate)}
                            </span>
                            {ps.settled ? (
                                <div className={styles['period-result']}>
                                    <span className={styles['period-delivery']}>{ps.deliveryCnt}건</span>
                                    <span className={`${styles['period-badge']} ${ps.achieved ? styles['achievement-achieved'] : styles['achievement-not-achieved']}`}>
                                        {ps.achieved ? '달성' : '미달성'}
                                    </span>
                                </div>
                            ) : (
                                <span className={`${styles['period-badge']} ${styles['period-pending']}`}>
                                    정산예정
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {mission.expectedPaymentDate && (isActive || mission.riderStatus === 'SETTLEMENT_EXPECTED') && (
                <div className={styles['payment-date-row']}>
                    <span className={styles['payment-label']}>지급예정일</span>
                    <span className={styles['payment-date']}>{formatDate(mission.expectedPaymentDate)}</span>
                </div>
            )}

            {mission.description && (
                <div className={styles['card-description']}>{mission.description}</div>
            )}
        </div>
    );
};

const MissionPage: React.FC = () => {
    const navigate = useNavigate();
    const { missionData, isLoading: rawIsLoading, error } = useMission();
    const isLoading = useDeferredLoading(rawIsLoading, 200);

    const activeMissions = missionData?.missions.filter(
        (m) => m.riderStatus === 'IN_PROGRESS' || m.riderStatus === 'UPCOMING'
    ) ?? [];

    const pastMissions = missionData?.missions.filter(
        (m) => m.riderStatus === 'COMPLETED' || m.riderStatus === 'SETTLEMENT_EXPECTED' || m.riderStatus === 'SETTLED'
    ) ?? [];

    return (
        <div className={`screen active ${styles['mission-screen']}`}>
            <div className={styles['mission-header']}>
                <div className={styles['header-top']}>
                    <button className={styles['back-btn']} onClick={() => navigate(-1)}>
                        <i className="fas fa-arrow-left" />
                    </button>
                    <h1 className={styles['page-title']}>프로모션 미션</h1>
                    <div style={{ width: 40 }} />
                </div>
            </div>

            <div className={styles['mission-container']}>
                {isLoading ? (
                    <div className={styles['loading-section']}>
                        <div className={styles['loading-spinner']} />
                        <p className={styles['loading-text']}>미션 정보를 불러오는 중...</p>
                    </div>
                ) : error ? (
                    <div className={styles['error-section']}>
                        <div className={styles['error-icon']}>⚠️</div>
                        <p className={styles['error-text']}>{error}</p>
                    </div>
                ) : !missionData || missionData.missions.length === 0 ? (
                    <div className={styles['empty-section']}>
                        <div className={styles['empty-icon']}>🏆</div>
                        <p className={styles['empty-text']}>진행 중인 미션이 없습니다.</p>
                    </div>
                ) : (
                    <>
                        {activeMissions.length > 0 && (
                            <section className={styles['section']}>
                                <h2 className={styles['section-title']}>
                                    <span className={styles['section-dot']} />
                                    진행중 / 예정 미션
                                </h2>
                                <div className={styles['card-list']}>
                                    {activeMissions.map((m) => (
                                        <MissionCard key={m.seqNo} mission={m} />
                                    ))}
                                </div>
                            </section>
                        )}

                        {pastMissions.length > 0 && (
                            <section className={styles['section']}>
                                <h2 className={styles['section-title']}>
                                    <span className={`${styles['section-dot']} ${styles['dot-gray']}`} />
                                    완료된 미션
                                </h2>
                                <div className={styles['card-list']}>
                                    {pastMissions.map((m) => (
                                        <MissionCard key={m.seqNo} mission={m} />
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </div>

            <BottomNavigation currentScreen="home" />
        </div>
    );
};

export default MissionPage;
